'use client';

import { useCallback } from 'react';

import { config } from '@ps/config/client';
import { auth } from '@ps/functions/firebase/auth';

/**
 * Toggle the "favorite" + "visible" flags on a purchased Wear-With-AI
 * media item.
 *
 * These endpoints exist in the swagger spec but the OpenAPI generator
 * drops the class methods (it only writes the request interfaces). So
 * we hand-call them with `fetch` against the exact same paths legacy
 * uses:
 *   POST /v1/stores/{storeId}/purchased-wear-with-ai-media/{id}:setFavorite
 *   POST /v1/stores/{storeId}/purchased-wear-with-ai-media/{id}:setVisible
 *
 * Bodies match the codegen'd request types (`favorite: boolean` and
 * `visible: boolean`).
 */
async function authHeader(): Promise<Record<string, string>> {
  const authInstance = auth();
  await authInstance.authStateReady();
  const token = (await authInstance.currentUser?.getIdToken()) ?? '';
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export function useToggleAvatarMeMediaFavorite() {
  return useCallback(
    async ({
      sellerId,
      id,
      favorite,
    }: {
      sellerId: string;
      id: string;
      favorite: boolean;
    }): Promise<boolean> => {
      try {
        const url = `${config.apiEndpoint}/v1/stores/${encodeURIComponent(sellerId)}/purchased-wear-with-ai-media/${encodeURIComponent(id)}:setFavorite`;
        const res = await fetch(url, {
          method: 'POST',
          headers: await authHeader(),
          body: JSON.stringify({ favorite }),
        });
        if (!res.ok) throw new Error(`setFavorite ${res.status}`);
        return true;
      } catch (err) {
        console.error('[useToggleAvatarMeMediaFavorite]', err);
        return false;
      }
    },
    [],
  );
}

export function useToggleAvatarMeMediaVisibility() {
  return useCallback(
    async ({
      sellerId,
      id,
      visible,
    }: {
      sellerId: string;
      id: string;
      visible: boolean;
    }): Promise<boolean> => {
      try {
        const url = `${config.apiEndpoint}/v1/stores/${encodeURIComponent(sellerId)}/purchased-wear-with-ai-media/${encodeURIComponent(id)}:setVisible`;
        const res = await fetch(url, {
          method: 'POST',
          headers: await authHeader(),
          body: JSON.stringify({ visible }),
        });
        if (!res.ok) throw new Error(`setVisible ${res.status}`);
        return true;
      } catch (err) {
        console.error('[useToggleAvatarMeMediaVisibility]', err);
        return false;
      }
    },
    [],
  );
}
